/**
 * Organization Service — calls hub proxy for org-related endpoints
 */
import type {
  OrgWithRole,
  Organization,
  CreateOrgPayload,
  UpdateOrgPayload,
  OrgMember,
  AddMemberPayload,
  UpdateMemberRolePayload,
  InviteMemberPayload,
  InviteMemberResponse,
  ResendInviteResponse,
  OrganizationInvitation,
} from "@/types/organization.types";
import type { ApiResponse, PaginatedResponse, EnrichedPaginatedResponse } from "@/types/api.types";




import { hubCall } from "@/lib/api/hub";
export const organizationService = {
  async getMyInvitations(): Promise<ApiResponse<OrganizationInvitation[]>> {
    return hubCall<ApiResponse<OrganizationInvitation[]>>("/organization-invitations/me");
  },

  async acceptInvitation(memberId: string): Promise<ApiResponse<OrganizationInvitation>> {
    return hubCall<ApiResponse<OrganizationInvitation>>(
      `/organization-invitations/${memberId}/accept`,
      { method: "POST" },
    );
  },

  async rejectInvitation(memberId: string): Promise<void> {
    await hubCall<null>(`/organization-invitations/${memberId}/reject`, { method: "POST" });
  },

  /** List current user's orgs with role + member count */
  async getMyOrganizations(): Promise<PaginatedResponse<OrgWithRole>> {
    return hubCall<PaginatedResponse<OrgWithRole>>("/organizations/me?limit=100");
  },

  /** Create a new organization (caller becomes owner) */
  async createOrganization(
    payload: CreateOrgPayload
  ): Promise<ApiResponse<{ id: string }>> {
    return hubCall<ApiResponse<{ id: string }>>("/organizations", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },

  /** Get organization detail */
  async getOrganization(id: string): Promise<ApiResponse<Organization>> {
    return hubCall<ApiResponse<Organization>>(`/organizations/${id}`);
  },

  /** Update organization */
  async updateOrganization(
    id: string,
    payload: UpdateOrgPayload
  ): Promise<ApiResponse<{ id: string }>> {
    return hubCall<ApiResponse<{ id: string }>>(`/organizations/${id}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    });
  },

  /** Delete organization (soft delete) */
  async deleteOrganization(
    id: string
  ): Promise<ApiResponse<{ id: string }>> {
    return hubCall<ApiResponse<{ id: string }>>(`/organizations/${id}`, {
      method: "DELETE",
    });
  },

  /** List members of an organization.
   *  `search` is an ILIKE substring matched server-side against email +
   *  first/last name; BE drops queries shorter than 3 chars (UI also debounces). */
  async getMembers(
    orgId: string,
    params?: { page?: number; limit?: number; role?: string; search?: string }
  ): Promise<EnrichedPaginatedResponse<OrgMember>> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set("page", String(params.page));
    if (params?.limit) searchParams.set("limit", String(params.limit));
    if (params?.role) searchParams.set("role", params.role);
    if (params?.search) searchParams.set("search", params.search);
    const qs = searchParams.toString();
    return hubCall<EnrichedPaginatedResponse<OrgMember>>(
      `/organizations/${orgId}/members${qs ? `?${qs}` : ""}`
    );
  },

  /** Add member to organization */
  async addMember(
    orgId: string,
    payload: AddMemberPayload
  ): Promise<ApiResponse<{ id: string }>> {
    return hubCall<ApiResponse<{ id: string }>>(
      `/organizations/${orgId}/members`,
      {
        method: "POST",
        body: JSON.stringify(payload),
      }
    );
  },

  /** Update member role */
  async updateMemberRole(
    orgId: string,
    memberId: string,
    payload: UpdateMemberRolePayload
  ): Promise<ApiResponse<{ id: string }>> {
    return hubCall<ApiResponse<{ id: string }>>(
      `/organizations/${orgId}/members/${memberId}`,
      {
        method: "PUT",
        body: JSON.stringify(payload),
      }
    );
  },

  /** Remove member from organization */
  async removeMember(
    orgId: string,
    memberId: string
  ): Promise<ApiResponse<{ id: string }>> {
    return hubCall<ApiResponse<{ id: string }>>(
      `/organizations/${orgId}/members/${memberId}`,
      {
        method: "DELETE",
      }
    );
  },

  /**
   * Invite a member by email. Hub forwards to builder-auth which sends the
   * signup email + creates a pending user. Returns the new (or existing) user
   * id, status (`invited` | `active`), email, and whether the email was sent
   * (`false` when the user is already active or throttled).
   */
  async inviteByEmail(
    orgId: string,
    payload: InviteMemberPayload
  ): Promise<ApiResponse<InviteMemberResponse>> {
    return hubCall<ApiResponse<InviteMemberResponse>>(
      `/organizations/${orgId}/members/invite`,
      {
        method: "POST",
        body: JSON.stringify(payload),
      }
    );
  },

  /**
   * Resend invite email for a pending member. 429 on throttle (response body
   * carries `retryAfterSeconds` so the UI can show a wait hint).
   */
  async resendInvite(
    orgId: string,
    memberId: string
  ): Promise<ApiResponse<ResendInviteResponse>> {
    return hubCall<ApiResponse<ResendInviteResponse>>(
      `/organizations/${orgId}/members/${memberId}/resend-invite`,
      {
        method: "POST",
      }
    );
  },

  /**
   * Cancel a pending invite — alias of `removeMember` because hub's DELETE
   * /members/:id dispatcher detects status='invited' and routes to the
   * cancel-invite path (clears auth pending user + hub.users + org_members).
   * Kept as a distinct method so call-sites read intent-fully.
   */
  async cancelInvite(
    orgId: string,
    memberId: string
  ): Promise<ApiResponse<{ id: string }>> {
    return hubCall<ApiResponse<{ id: string }>>(
      `/organizations/${orgId}/members/${memberId}`,
      { method: "DELETE" }
    );
  },
};
